import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { SelectDropdown } from 'src/components/Reusable/SelectDropdown'
import type { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { Message } from 'src/components/Reusable/Message/Message'
import { MessageType } from 'src/components/Reusable/Message/Message.constants'
import { InfoTooltip } from 'src/components/Reusable/Tooltip/InfoTooltip'
import { TooltipPositions } from 'src/components/Reusable/Tooltip/Tooltip'
import { DropdownPrimaryButton } from 'src/components/Reusable/Buttons'
import { DropdownButtonItems } from 'src/components/Reusable/Buttons/DropdownPrimaryButton/DropdownPrimaryButton'
import { Spinner } from 'src/Throbber'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import {
  ErrorText,
  FormBody,
  Input,
  Label,
  Row,
  Textarea,
} from './AddWorkflowEvidenceForm.styles'

export type Option = {
  name: string
  pid: string
  pname: string
  tid: string
  tname: string
  tpid: string
  receptorId?: string
  receptorDisplayName?: string
}

export const NO_PRESET_VALUE = '__no_preset__'

export type WorkflowFormState = {
  receptorId: string
  project: string
  taskType: string
  taskName: string
  title: string
  description: string
  assigneeId: string
  useNoPreset: boolean
  saveAsPreset: boolean
  siteUrl: string
  label: string
}

export type AddWorkflowEvidenceFormProps = {
  modalId: ModalFormId
  isShown: boolean
  onSubmitFunc: (data: WorkflowFormState) => Promise<void> | void
  onStateChange?: (data: WorkflowFormState) => void
  configs?: Option[]
  siteUrl: string
  label: string
  controlId: string
  description: string
  initialTitle?: string
  name?: string
  receptorDisplayName?: string
  configureActions?: Array<{
    receptorId: string
    label: string
  }>
  onConfigureAction?: (receptorId: string) => void
  onConfigureLinkClick?: (receptorId: string) => void
  showTicketDetails?: boolean
  ticketDetailsLabel?: string
  assigneeItems?: SelectItem[]
  assigneeLoading?: boolean
  requireAssignee?: boolean
  ticketError?: string
  noPresetMode?: boolean
  noPresetInstanceItems?: SelectItem[]
  noPresetInstanceDefaultValues?: SelectItem[]
  onSelectNoPresetInstance?: (selected: SelectItem[]) => void
  noPresetProjectItems?: SelectItem[]
  noPresetProjectDefaultValues?: SelectItem[]
  onSelectNoPresetProject?: (selected: SelectItem[]) => void
  noPresetTaskTypeItems?: SelectItem[]
  noPresetTaskTypeDefaultValues?: SelectItem[]
  onSelectNoPresetTaskType?: (selected: SelectItem[]) => void
  hasConfiguredPresets?: boolean
  preselectedReceptorId?: string
}

export const AddWorkflowEvidenceForm: React.FC<
  AddWorkflowEvidenceFormProps
> = ({
  modalId,
  isShown,
  onSubmitFunc,
  onStateChange,
  configs = [],
  siteUrl = '',
  label = '',
  controlId = '',
  description = '',
  initialTitle = '',
  name = '',
  receptorDisplayName = '',
  configureActions = [],
  onConfigureAction,
  onConfigureLinkClick,
  showTicketDetails = false,
  ticketDetailsLabel = '',
  assigneeItems = [],
  assigneeLoading = false,
  requireAssignee = false,
  ticketError = '',
  noPresetMode = false,
  noPresetInstanceItems = [],
  noPresetInstanceDefaultValues = [],
  onSelectNoPresetInstance,
  noPresetProjectItems = [],
  noPresetProjectDefaultValues = [],
  onSelectNoPresetProject,
  noPresetTaskTypeItems = [],
  noPresetTaskTypeDefaultValues = [],
  onSelectNoPresetTaskType,
  hasConfiguredPresets = true,
  preselectedReceptorId = '',
}) => {
  const makeInitial = useCallback(
    (
      nextSiteUrl?: string,
      nextLabel?: string,
      nextControlId?: string,
    ): WorkflowFormState => ({
      receptorId: '',
      project: '',
      taskType: '',
      taskName: '',
      title:
        initialTitle?.trim() ||
        (nextControlId
          ? `[${nextControlId}] Request for evidence`
          : 'Request for evidence'),
      description: description || '',
      assigneeId: '',
      useNoPreset: false,
      saveAsPreset: false,
      siteUrl: nextSiteUrl || '',
      label: nextLabel || '',
    }),
    [description, initialTitle],
  )

  const [state, setState] = useState<WorkflowFormState>(() =>
    makeInitial(siteUrl, label, controlId),
  )

  const selectedIndex = useMemo(
    () =>
      configs.findIndex(
        (c) =>
          c.pid === state.project &&
          c.tid === state.taskType &&
          (c.receptorId ?? '') === state.receptorId,
      ),
    [configs, state.project, state.taskType, state.receptorId],
  )

  useEffect(() => {
    const next = makeInitial(siteUrl, label, controlId)
    if (!isShown) {
      setState(next)
    } else {
      setState((prev) => ({
        ...prev,
        title: next.title,
        description: next.description,
        siteUrl: next.siteUrl,
        label: next.label,
      }))
    }
  }, [
    isShown,
    siteUrl,
    label,
    controlId,
    description,
    initialTitle,
    makeInitial,
  ])

  useEffect(() => {
    if (state.useNoPreset) return
    if (configs.length > 0 && (!state.project || !state.taskType)) {
      setState((s) => ({
        ...s,
        receptorId: s.receptorId || configs[0].receptorId || '',
        project: s.project || configs[0].pid,
        taskType: s.taskType || configs[0].tid,
        taskName: s.taskName || configs[0].tname,
      }))
    }
  }, [configs, state.project, state.taskType, state.useNoPreset])

  useEffect(() => {
    if (!isShown) return
    const selectedReceptorId = preselectedReceptorId.trim()
    if (!selectedReceptorId) return

    const presetForSelectedReceptor = configs.find(
      (c) =>
        (c.receptorId ?? '') === selectedReceptorId &&
        c.receptorId !== NO_PRESET_VALUE,
    )

    if (presetForSelectedReceptor) {
      setState((s) => ({
        ...s,
        useNoPreset: false,
        saveAsPreset: false,
        receptorId: selectedReceptorId,
        project: presetForSelectedReceptor.pid,
        taskType: presetForSelectedReceptor.tid,
        taskName: presetForSelectedReceptor.tname,
        assigneeId: '',
      }))
      return
    }

    setState((s) => ({
      ...s,
      useNoPreset: true,
      saveAsPreset: false,
      receptorId: selectedReceptorId,
      project: '',
      taskType: '',
      taskName: '',
      assigneeId: '',
    }))
  }, [isShown, preselectedReceptorId, configs])

  useEffect(() => {
    if (state.useNoPreset) return
    if (selectedIndex < 0) {
      setState((s) => ({
        ...s,
        receptorId: '',
        project: '',
        taskType: '',
        taskName: '',
        useNoPreset: false,
        saveAsPreset: false,
      }))
    }
  }, [selectedIndex, state.useNoPreset])

  useEffect(() => {
    if (
      !showTicketDetails ||
      !requireAssignee ||
      state.assigneeId ||
      assigneeItems.length === 0
    ) {
      return
    }
    setState((s) => ({
      ...s,
      assigneeId: String(assigneeItems[0].value ?? ''),
    }))
  }, [showTicketDetails, requireAssignee, state.assigneeId, assigneeItems])

  useEffect(() => {
    onStateChange?.(state)
  }, [state, onStateChange])

  const getConfigLabel = useCallback(
    (option: Option): string => {
      if (option.receptorId === NO_PRESET_VALUE) {
        return 'No Preset'
      }
      const receptorName =
        option.receptorDisplayName?.trim() ||
        receptorDisplayName.trim() ||
        name.trim()
      const receptorPrefix = receptorName ? `${receptorName} - ` : ''
      return `${option.name} (${receptorPrefix}${option.pname} - ${option.tname})`
    },
    [name, receptorDisplayName],
  )

  const configItems: SelectItem[] = useMemo(
    () =>
      configs.map((c, i) => ({
        name: getConfigLabel(c),
        value: i,
      })),
    [configs, getConfigLabel],
  )

  const configDefaultValues: SelectItem[] = useMemo(() => {
    if (selectedIndex < 0 || !configs[selectedIndex]) return []
    const c = configs[selectedIndex]
    return [{ name: getConfigLabel(c), value: selectedIndex }]
  }, [configs, getConfigLabel, selectedIndex])

  const selectedConfig = selectedIndex >= 0 ? configs[selectedIndex] : undefined
  const selectedNoPreset = selectedConfig?.receptorId === NO_PRESET_VALUE
  const effectiveNoPresetMode = noPresetMode || selectedNoPreset

  useEffect(() => {
    if (!effectiveNoPresetMode) return
    if (state.receptorId) return
    if (noPresetInstanceItems.length !== 1) return

    const onlyInstanceId = String(noPresetInstanceItems[0]?.value ?? '')
    if (!onlyInstanceId) return

    setState((s) => ({
      ...s,
      useNoPreset: true,
      receptorId: onlyInstanceId,
    }))
  }, [effectiveNoPresetMode, state.receptorId, noPresetInstanceItems])

  const assigneeDefaultValues: SelectItem[] = useMemo(() => {
    if (!state.assigneeId) return []
    const selected = assigneeItems.find(
      (item) => String(item.value) === state.assigneeId,
    )
    return selected ? [selected] : []
  }, [state.assigneeId, assigneeItems])

  const handleConfigSelect = useCallback(
    (selected: SelectItem[]) => {
      const item = selected[0]
      if (item == null) return
      const idx = Number(item.value)
      const sel = configs[idx]
      if (!sel) return
      if (sel.receptorId === NO_PRESET_VALUE) {
        const onlyInstanceId =
          noPresetInstanceItems.length === 1
            ? String(noPresetInstanceItems[0]?.value ?? '')
            : ''
        setState((s) => ({
          ...s,
          useNoPreset: true,
          receptorId: onlyInstanceId,
          project: '',
          taskType: '',
          taskName: '',
          assigneeId: '',
          saveAsPreset: false,
        }))
        return
      }
      setState((s) => ({
        ...s,
        useNoPreset: false,
        receptorId: sel.receptorId || '',
        project: sel.pid,
        taskType: sel.tid,
        taskName: sel.tname,
        saveAsPreset: false,
      }))
    },
    [configs, noPresetInstanceItems],
  )

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (showTicketDetails && requireAssignee && !state.assigneeId?.trim())
      return
    if (!state.receptorId || !state.project || !state.title?.trim()) return
    await onSubmitFunc(state)
  }

  const configureDropdownItems: DropdownButtonItems[] = useMemo(
    () =>
      configureActions.map((action) => ({
        name: action.label,
        onClick: (e: React.MouseEvent) => {
          e.preventDefault()
          e.stopPropagation()
          onConfigureAction?.(action.receptorId)
        },
      })),
    [configureActions, onConfigureAction],
  )

  const onConfigureFromFlavor = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const selectedInstanceId =
        state.receptorId ||
        String(noPresetInstanceDefaultValues[0]?.value ?? '') ||
        configureActions[0]?.receptorId ||
        ''
      if (!selectedInstanceId) return
      onConfigureLinkClick?.(selectedInstanceId)
    },
    [
      state.receptorId,
      noPresetInstanceDefaultValues,
      configureActions,
      onConfigureLinkClick,
    ],
  )

  return (
    <FormBody id={modalId} onSubmit={onSubmit}>
      {configs.length > 0 ? (
        <>
          <Row>
            <Label>
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                Preset
                <InfoTooltip
                  id="workflow-preset-tooltip"
                  placement={TooltipPositions.right}
                  body="Workflow presets come from your receptor configuration."
                  noMargin
                />
              </span>
            </Label>
            <SelectDropdown
              label=""
              items={configItems}
              defaultValues={configDefaultValues}
              onSelectCb={handleConfigSelect}
              placeholder="Select preset to fill in task information (optional)"
              hideError
            />
          </Row>
          {!hasConfiguredPresets ? (
            <Row>
              <div />
              <div style={{ fontSize: 12, color: '#4b5563' }}>
                No Presets found, save the current task settings as preset via
                {' "Save as preset" '}or by{' '}
                <button
                  type="button"
                  onClick={onConfigureFromFlavor}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    margin: 0,
                    color: '#2563eb',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  configuring the receptor
                </button>
              </div>
            </Row>
          ) : null}
          {showTicketDetails ? (
            <>
              {effectiveNoPresetMode ? (
                <>
                  <Row>
                    <Label>Instance</Label>
                    <SelectDropdown
                      label=""
                      items={noPresetInstanceItems}
                      defaultValues={noPresetInstanceDefaultValues}
                      onSelectCb={(selected: SelectItem[]) => {
                        const item = selected?.[0]
                        const receptorId = item ? String(item.value ?? '') : ''
                        setState((s) => ({
                          ...s,
                          useNoPreset: true,
                          receptorId,
                          project: '',
                          taskType: '',
                          taskName: '',
                          assigneeId: '',
                          saveAsPreset: s.saveAsPreset,
                        }))
                        onSelectNoPresetInstance?.(selected)
                      }}
                      placeholder="Select instance"
                      hideError
                    />
                  </Row>
                  <Row>
                    <Label>Project</Label>
                    <SelectDropdown
                      label=""
                      items={noPresetProjectItems}
                      defaultValues={noPresetProjectDefaultValues}
                      onSelectCb={(selected: SelectItem[]) => {
                        const item = selected?.[0]
                        const projectId = item ? String(item.value ?? '') : ''
                        setState((s) => ({
                          ...s,
                          useNoPreset: true,
                          project: projectId,
                          taskType: '',
                          taskName: '',
                          assigneeId: '',
                          saveAsPreset: s.saveAsPreset,
                        }))
                        onSelectNoPresetProject?.(selected)
                      }}
                      placeholder="Select project"
                      hideError
                    />
                  </Row>
                  <Row>
                    <Label>Task Type</Label>
                    <SelectDropdown
                      label=""
                      items={noPresetTaskTypeItems}
                      defaultValues={noPresetTaskTypeDefaultValues}
                      onSelectCb={(selected: SelectItem[]) => {
                        const item = selected?.[0]
                        const taskTypeId = item ? String(item.value ?? '') : ''
                        const taskTypeName = item ? String(item.name ?? '') : ''
                        setState((s) => ({
                          ...s,
                          useNoPreset: true,
                          taskType: taskTypeId,
                          taskName: taskTypeName,
                          assigneeId: '',
                          saveAsPreset: s.saveAsPreset,
                        }))
                        onSelectNoPresetTaskType?.(selected)
                      }}
                      placeholder="Select task type"
                      hideError
                    />
                  </Row>
                </>
              ) : (
                <>
                  <Row>
                    <Label htmlFor="workflow-ticket-project">Project</Label>
                    <Input
                      id="workflow-ticket-project"
                      value={selectedConfig?.pname || state.project}
                      readOnly
                    />
                  </Row>
                  <Row>
                    <Label htmlFor="workflow-ticket-task-type">Task Type</Label>
                    <Input
                      id="workflow-ticket-task-type"
                      value={selectedConfig?.tname || state.taskType}
                      readOnly
                    />
                  </Row>
                </>
              )}
              <Row>
                <Label htmlFor="workflow-ticket-title">Title</Label>
                <Input
                  id="workflow-ticket-title"
                  value={state.title}
                  onChange={(e) =>
                    setState((s) => ({ ...s, title: e.target.value }))
                  }
                  placeholder="Request for Evidence"
                />
              </Row>
              <Row>
                <Label htmlFor="workflow-ticket-description">Description</Label>
                <Textarea
                  id="workflow-ticket-description"
                  value={state.description}
                  onChange={(e) =>
                    setState((s) => ({ ...s, description: e.target.value }))
                  }
                  placeholder="Required evidence details"
                />
              </Row>
              <Row>
                <Label>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    Assignee
                    {assigneeLoading ? (
                      <Spinner size="s" color="primary" />
                    ) : null}
                  </span>
                </Label>
                <SelectDropdown
                  label=""
                  items={assigneeItems}
                  defaultValues={assigneeDefaultValues}
                  onSelectCb={(selected: SelectItem[]) => {
                    const item = selected?.[0]
                    if (!item) return
                    setState((s) => ({
                      ...s,
                      assigneeId: String(item.value ?? ''),
                    }))
                  }}
                  placeholder="Select assignee"
                  hideError
                />
              </Row>
              <Row>
                <Label htmlFor="workflow-ticket-label">Label</Label>
                <Input
                  id="workflow-ticket-label"
                  value={ticketDetailsLabel}
                  readOnly
                />
              </Row>
              {effectiveNoPresetMode ? (
                <Row>
                  <div />
                  <CheckBoxInput
                    id="workflow-save-as-preset"
                    label="Save as preset"
                    checked={state.saveAsPreset}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        saveAsPreset: e.currentTarget.checked,
                      }))
                    }
                  />
                </Row>
              ) : null}
              {ticketError ? <ErrorText>{ticketError}</ErrorText> : null}
            </>
          ) : null}
        </>
      ) : noPresetMode ? (
        <>
          <Message
            messageType={MessageType.INFO}
            messageText={`No ${name} presets found. Choose an instance, project, and task type.`}
            fullWidth
            ctaContent={
              configureDropdownItems.length > 0 ? (
                <DropdownPrimaryButton
                  label={`Create ${name} preset`}
                  items={configureDropdownItems}
                />
              ) : undefined
            }
          />
          <Row>
            <Label>Instance</Label>
            <SelectDropdown
              label=""
              items={noPresetInstanceItems}
              defaultValues={noPresetInstanceDefaultValues}
              onSelectCb={(selected: SelectItem[]) => {
                const item = selected?.[0]
                const receptorId = item ? String(item.value ?? '') : ''
                setState((s) => ({
                  ...s,
                  useNoPreset: true,
                  receptorId,
                  project: '',
                  taskType: '',
                  taskName: '',
                  assigneeId: '',
                  saveAsPreset: s.saveAsPreset,
                }))
                onSelectNoPresetInstance?.(selected)
              }}
              placeholder="Select instance"
              hideError
            />
          </Row>
          <Row>
            <Label>Project</Label>
            <SelectDropdown
              label=""
              items={noPresetProjectItems}
              defaultValues={noPresetProjectDefaultValues}
              onSelectCb={(selected: SelectItem[]) => {
                const item = selected?.[0]
                const projectId = item ? String(item.value ?? '') : ''
                setState((s) => ({
                  ...s,
                  useNoPreset: true,
                  project: projectId,
                  taskType: '',
                  taskName: '',
                  assigneeId: '',
                  saveAsPreset: s.saveAsPreset,
                }))
                onSelectNoPresetProject?.(selected)
              }}
              placeholder="Select project"
              hideError
            />
          </Row>
          <Row>
            <Label>Task Type</Label>
            <SelectDropdown
              label=""
              items={noPresetTaskTypeItems}
              defaultValues={noPresetTaskTypeDefaultValues}
              onSelectCb={(selected: SelectItem[]) => {
                const item = selected?.[0]
                const taskTypeId = item ? String(item.value ?? '') : ''
                const taskTypeName = item ? String(item.name ?? '') : ''
                setState((s) => ({
                  ...s,
                  useNoPreset: true,
                  taskType: taskTypeId,
                  taskName: taskTypeName,
                  assigneeId: '',
                  saveAsPreset: s.saveAsPreset,
                }))
                onSelectNoPresetTaskType?.(selected)
              }}
              placeholder="Select task type"
              hideError
            />
          </Row>
          {showTicketDetails ? (
            <>
              <Row>
                <Label htmlFor="workflow-ticket-title">Title</Label>
                <Input
                  id="workflow-ticket-title"
                  value={state.title}
                  onChange={(e) =>
                    setState((s) => ({ ...s, title: e.target.value }))
                  }
                  placeholder="Request for Evidence"
                />
              </Row>
              <Row>
                <Label htmlFor="workflow-ticket-description">Description</Label>
                <Textarea
                  id="workflow-ticket-description"
                  value={state.description}
                  onChange={(e) =>
                    setState((s) => ({ ...s, description: e.target.value }))
                  }
                  placeholder="Required evidence details"
                />
              </Row>
              <Row>
                <Label>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    Assignee
                    {assigneeLoading ? (
                      <Spinner size="s" color="primary" />
                    ) : null}
                  </span>
                </Label>
                <SelectDropdown
                  label=""
                  items={assigneeItems}
                  defaultValues={assigneeDefaultValues}
                  onSelectCb={(selected: SelectItem[]) => {
                    const item = selected?.[0]
                    if (!item) return
                    setState((s) => ({
                      ...s,
                      assigneeId: String(item.value ?? ''),
                    }))
                  }}
                  placeholder="Select assignee"
                  hideError
                />
              </Row>
              <Row>
                <Label htmlFor="workflow-ticket-label">Label</Label>
                <Input
                  id="workflow-ticket-label"
                  value={ticketDetailsLabel}
                  readOnly
                />
              </Row>
              <Row>
                <div />
                <CheckBoxInput
                  id="workflow-save-as-preset-empty"
                  label="Save as preset"
                  checked={state.saveAsPreset}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      saveAsPreset: e.currentTarget.checked,
                    }))
                  }
                />
              </Row>
              {ticketError ? <ErrorText>{ticketError}</ErrorText> : null}
            </>
          ) : null}
        </>
      ) : (
        <>
          <Message
            messageType={MessageType.ERROR}
            messageText={`No ${name} presets found`}
            fullWidth
            ctaContent={
              configureDropdownItems.length > 0 ? (
                <DropdownPrimaryButton
                  label={`Create ${name} preset`}
                  items={configureDropdownItems}
                />
              ) : undefined
            }
          />
        </>
      )}
    </FormBody>
  )
}
